/**
 * Vec2D - 2D Vector Math Class
 * Ported from cc.sketchchair.sketch.Vec2D.java
 *
 * Provides comprehensive 2D vector operations for the sketch engine.
 * This is a direct port of the Java version to maintain compatibility.
 */

export class Vec2D {
    // Static axis constants
    static X_AXIS = new Vec2D(1, 0);
    static Y_AXIS = new Vec2D(0, 1);

    /**
     * Create vector from angle (polar to cartesian)
     * @param {number} theta - Angle in radians
     * @returns {Vec2D} New vector
     */
    static fromTheta(theta) {
        return new Vec2D(Math.cos(theta), Math.sin(theta));
    }

    /**
     * Create a new 2D vector
     * @param {number|Vec2D} [x=0] - X coordinate or Vec2D to copy
     * @param {number} [y=0] - Y coordinate
     */
    constructor(x = 0, y = 0) {
        if (x instanceof Vec2D) {
            // Copy constructor
            this.x = x.x;
            this.y = x.y;
        } else {
            this.x = x;
            this.y = y;
        }
    }

    /**
     * Create a copy of this vector
     * @returns {Vec2D} New vector with same coordinates
     */
    clone() {
        return new Vec2D(this.x, this.y);
    }

    // ============================================
    // Addition
    // ============================================

    /**
     * Add coordinates and return as new vector
     * @param {number|Vec2D} a - X coordinate or Vec2D
     * @param {number} [b] - Y coordinate
     * @returns {Vec2D} New vector
     */
    add(a, b) {
        if (a instanceof Vec2D) {
            return new Vec2D(this.x + a.x, this.y + a.y);
        }
        return new Vec2D(this.x + a, this.y + b);
    }

    /**
     * Add coordinates and modify self
     * @param {number|Vec2D} a - X coordinate or Vec2D
     * @param {number} [b] - Y coordinate
     * @returns {Vec2D} This vector
     */
    addSelf(a, b) {
        if (a instanceof Vec2D) {
            this.x += a.x;
            this.y += a.y;
        } else {
            this.x += a;
            this.y += b;
        }
        return this;
    }

    // ============================================
    // Subtraction
    // ============================================

    /**
     * Subtract coordinates and return as new vector
     * @param {number|Vec2D} a - X coordinate or Vec2D
     * @param {number} [b] - Y coordinate
     * @returns {Vec2D} New vector
     */
    sub(a, b) {
        if (a instanceof Vec2D) {
            return new Vec2D(this.x - a.x, this.y - a.y);
        }
        return new Vec2D(this.x - a, this.y - b);
    }

    /**
     * Subtract coordinates and modify self
     * @param {number|Vec2D} a - X coordinate or Vec2D
     * @param {number} [b] - Y coordinate
     * @returns {Vec2D} This vector
     */
    subSelf(a, b) {
        if (a instanceof Vec2D) {
            this.x -= a.x;
            this.y -= a.y;
        } else {
            this.x -= a;
            this.y -= b;
        }
        return this;
    }

    // ============================================
    // Scaling
    // ============================================

    /**
     * Scale uniformly and return new vector
     * @param {number|Vec2D} s - Scale factor or scale vector
     * @param {number} [b] - Y scale (if s is number)
     * @returns {Vec2D} New vector
     */
    scale(s, b) {
        if (s instanceof Vec2D) {
            return new Vec2D(this.x * s.x, this.y * s.y);
        } else if (b !== undefined) {
            return new Vec2D(this.x * s, this.y * b);
        }
        return new Vec2D(this.x * s, this.y * s);
    }

    /**
     * Scale uniformly and modify self
     * @param {number|Vec2D} s - Scale factor or scale vector
     * @param {number} [b] - Y scale (if s is number)
     * @returns {Vec2D} This vector
     */
    scaleSelf(s, b) {
        if (s instanceof Vec2D) {
            this.x *= s.x;
            this.y *= s.y;
        } else if (b !== undefined) {
            this.x *= s;
            this.y *= b;
        } else {
            this.x *= s;
            this.y *= s;
        }
        return this;
    }

    // ============================================
    // Magnitude / Length
    // ============================================

    /**
     * Calculate magnitude (length) of vector
     * @returns {number} Vector length
     */
    magnitude() {
        return Math.sqrt(this.x * this.x + this.y * this.y);
    }

    /**
     * Calculate squared magnitude (faster, no sqrt)
     * @returns {number} Squared length
     */
    magSquared() {
        return this.x * this.x + this.y * this.y;
    }

    // ============================================
    // Normalization
    // ============================================

    /**
     * Normalize vector (make length = 1)
     * @returns {Vec2D} This vector
     */
    normalize() {
        const mag = this.x * this.x + this.y * this.y;
        if (mag > 0) {
            const invMag = 1.0 / Math.sqrt(mag);
            this.x *= invMag;
            this.y *= invMag;
        }
        return this;
    }

    /**
     * Get normalized copy
     * @returns {Vec2D} New normalized vector
     */
    getNormalized() {
        return new Vec2D(this).normalize();
    }

    /**
     * Normalize to specific length
     * @param {number} len - Desired length
     * @returns {Vec2D} This vector
     */
    normalizeTo(len) {
        return this.normalize().scaleSelf(len);
    }

    /**
     * Get normalized copy with specific length
     * @param {number} len - Desired length
     * @returns {Vec2D} New vector
     */
    getNormalizedTo(len) {
        return this.getNormalized().scaleSelf(len);
    }

    // ============================================
    // Distance
    // ============================================

    /**
     * Calculate distance to another vector
     * @param {Vec2D} v - Other vector
     * @returns {number} Distance
     */
    distanceTo(v) {
        if (!v) return 0;
        const dx = this.x - v.x;
        const dy = this.y - v.y;
        return Math.sqrt(dx * dx + dy * dy);
    }

    /**
     * Calculate squared distance (faster, no sqrt)
     * @param {Vec2D} v - Other vector
     * @returns {number} Squared distance
     */
    distanceToSquared(v) {
        if (!v) return 0;
        const dx = this.x - v.x;
        const dy = this.y - v.y;
        return dx * dx + dy * dy;
    }

    // ============================================
    // Dot & Cross Product
    // ============================================

    /**
     * Calculate dot product
     * @param {Vec2D} v - Other vector
     * @returns {number} Dot product
     */
    dot(v) {
        return this.x * v.x + this.y * v.y;
    }

    /**
     * Calculate cross product (2D)
     * Returns the Z component of the 3D cross product
     * @param {Vec2D} v - Other vector
     * @returns {number} Cross product magnitude
     */
    cross(v) {
        return this.x * v.y - this.y * v.x;
    }

    // ============================================
    // Angles
    // ============================================

    /**
     * Get heading angle (direction in XY plane)
     * @returns {number} Angle in radians
     */
    heading() {
        return Math.atan2(this.y, this.x);
    }

    /**
     * Compute angle between this and another vector (normalized)
     * @param {Vec2D} v - Other vector
     * @returns {number} Angle in radians
     */
    angleBetween(v, forceNormalize = false) {
        if (forceNormalize) {
            const theta = this.getNormalized().dot(v.getNormalized());
            return Math.acos(theta);
        }
        return Math.acos(this.dot(v));
    }

    // ============================================
    // Rotation
    // ============================================

    /**
     * Rotate vector by angle
     * @param {number} theta - Angle in radians
     * @returns {Vec2D} This vector
     */
    rotate(theta) {
        const co = Math.cos(theta);
        const si = Math.sin(theta);
        const xx = co * this.x - si * this.y;
        this.y = si * this.x + co * this.y;
        this.x = xx;
        return this;
    }

    /**
     * Get rotated copy
     * @param {number} theta - Angle in radians
     * @returns {Vec2D} New rotated vector
     */
    getRotated(theta) {
        return new Vec2D(this).rotate(theta);
    }

    // ============================================
    // Interpolation
    // ============================================

    /**
     * Linear interpolation to another vector
     * @param {Vec2D} v - Target vector
     * @param {number} f - Interpolation factor (0-1)
     * @returns {Vec2D} New interpolated vector
     */
    interpolateTo(v, f) {
        return new Vec2D(
            this.x + (v.x - this.x) * f,
            this.y + (v.y - this.y) * f
        );
    }

    /**
     * Linear interpolation to another vector (modify self)
     * @param {Vec2D} v - Target vector
     * @param {number} f - Interpolation factor (0-1)
     * @returns {Vec2D} This vector
     */
    interpolateToSelf(v, f) {
        this.x += (v.x - this.x) * f;
        this.y += (v.y - this.y) * f;
        return this;
    }

    // ============================================
    // Perpendicular & Inversion
    // ============================================

    /**
     * Make perpendicular (rotate 90 degrees)
     * @returns {Vec2D} This vector
     */
    perpendicular() {
        const t = this.x;
        this.x = -this.y;
        this.y = t;
        return this;
    }

    /**
     * Get perpendicular copy
     * @returns {Vec2D} New perpendicular vector
     */
    getPerpendicular() {
        return new Vec2D(this).perpendicular();
    }

    /**
     * Invert vector (multiply by -1)
     * @returns {Vec2D} This vector
     */
    invert() {
        this.x *= -1;
        this.y *= -1;
        return this;
    }

    /**
     * Get inverted copy
     * @returns {Vec2D} New inverted vector
     */
    getInverted() {
        return new Vec2D(-this.x, -this.y);
    }

    // ============================================
    // Limiting & Clamping
    // ============================================

    /**
     * Limit magnitude to maximum length
     * @param {number} lim - Maximum length
     * @returns {Vec2D} This vector
     */
    limit(lim) {
        if (this.magSquared() > lim * lim) {
            return this.normalize().scaleSelf(lim);
        }
        return this;
    }

    /**
     * Get limited copy
     * @param {number} lim - Maximum length
     * @returns {Vec2D} New limited vector
     */
    getLimited(lim) {
        if (this.magSquared() > lim * lim) {
            return this.getNormalized().scaleSelf(lim);
        }
        return new Vec2D(this);
    }

    // ============================================
    // Geometric Queries
    // ============================================

    /**
     * Find closest point on line segment
     * @param {Vec2D} a - Line start
     * @param {Vec2D} b - Line end
     * @returns {Vec2D} Closest point
     */
    closestPointOnLine(a, b) {
        const v = b.sub(a);
        const t = this.sub(a).dot(v) / v.magSquared();

        if (t < 0.0) return a;
        if (t > 1.0) return b;

        return a.add(v.scaleSelf(t));
    }

    /**
     * Find closest point on triangle
     * @param {Vec2D} a - Triangle vertex
     * @param {Vec2D} b - Triangle vertex
     * @param {Vec2D} c - Triangle vertex
     * @returns {Vec2D} Closest point
     */
    closestPointOnTriangle(a, b, c) {
        const Rab = this.closestPointOnLine(a, b);
        const Rbc = this.closestPointOnLine(b, c);
        const Rca = this.closestPointOnLine(c, a);

        const dAB = this.sub(Rab).magnitude();
        const dBC = this.sub(Rbc).magnitude();
        const dCA = this.sub(Rca).magnitude();

        let min = dAB;
        let result = Rab;

        if (dBC < min) {
            min = dBC;
            result = Rbc;
        }
        if (dCA < min) {
            result = Rca;
        }

        return result;
    }

    /**
     * Check if point is inside circle
     * @param {Vec2D} circleOrigin - Circle center
     * @param {number} circleRadius - Circle radius
     * @returns {boolean} True if inside
     */
    isInCircle(circleOrigin, circleRadius) {
        const d = this.sub(circleOrigin).magSquared();
        return d <= circleRadius * circleRadius;
    }

    /**
     * Ray-circle intersection
     * @param {Vec2D} rayDir - Ray direction (normalized)
     * @param {Vec2D} circleOrigin - Circle center
     * @param {number} circleRadius - Circle radius
     * @returns {number} Distance to intersection, -1 if none
     */
    intersectRayCircle(rayDir, circleOrigin, circleRadius) {
        const q = circleOrigin.sub(this);
        const distSquared = q.magSquared();
        const v = q.dot(rayDir);
        const d = circleRadius * circleRadius - (distSquared - v * v);

        if (d < 0.0) {
            return -1;
        }

        return v - Math.sqrt(d);
    }

    // ============================================
    // Utility
    // ============================================

    /**
     * Set coordinates
     * @param {number|Vec2D} x - X coordinate or Vec2D
     * @param {number} [y] - Y coordinate
     * @returns {Vec2D} This vector
     */
    set(x, y) {
        if (x instanceof Vec2D) {
            this.x = x.x;
            this.y = x.y;
        } else {
            this.x = x;
            this.y = y;
        }
        return this;
    }

    /**
     * Clear vector (set to 0, 0)
     * @returns {Vec2D} This vector
     */
    clear() {
        this.x = 0;
        this.y = 0;
        return this;
    }

    /**
     * Check if zero vector
     * @returns {boolean} True if x=0 and y=0
     */
    isZeroVector() {
        return this.x === 0 && this.y === 0;
    }

    /**
     * Copy vector
     * @returns {Vec2D} New copy
     */
    copy() {
        return new Vec2D(this);
    }

    /**
     * Compare vectors
     * @param {Object} obj - Object to compare
     * @returns {boolean} True if equal
     */
    equals(obj) {
        if (obj instanceof Vec2D) {
            return this.x === obj.x && this.y === obj.y;
        }
        return false;
    }

    /**
     * Compare magnitude
     * @param {Vec2D} v - Vector to compare
     * @returns {number} -1, 0, or 1
     */
    compareTo(v) {
        if (this.x === v.x && this.y === v.y) {
            return 0;
        }
        return this.magSquared() - v.magSquared();
    }

    /**
     * Get signum vector (-1, 0, or 1 for each component)
     * @returns {Vec2D} This vector
     */
    signum() {
        this.x = this.x < 0 ? -1 : (this.x === 0 ? 0 : 1);
        this.y = this.y < 0 ? -1 : (this.y === 0 ? 0 : 1);
        return this;
    }

    /**
     * Get signum copy
     * @returns {Vec2D} New vector
     */
    getSignum() {
        return new Vec2D(this).signum();
    }

    /**
     * Get reciprocal
     * @returns {Vec2D} This vector
     */
    reciprocal() {
        this.x = 1.0 / this.x;
        this.y = 1.0 / this.y;
        return this;
    }

    /**
     * Get reciprocal copy
     * @returns {Vec2D} New vector
     */
    getReciprocal() {
        return this.copy().reciprocal();
    }

    // ============================================
    // Coordinate Conversion
    // ============================================

    /**
     * Convert to cartesian (from polar)
     * @returns {Vec2D} This vector
     */
    toCartesian() {
        const xx = this.x * Math.cos(this.y);
        this.y = this.x * Math.sin(this.y);
        this.x = xx;
        return this;
    }

    /**
     * Convert to polar (from cartesian)
     * @returns {Vec2D} This vector
     */
    toPolar() {
        const r = Math.sqrt(this.x * this.x + this.y * this.y);
        this.y = Math.atan2(this.y, this.x);
        this.x = r;
        return this;
    }

    /**
     * Tangent normal of ellipse at this point
     * @param {Vec2D} ellipseOrigin - Ellipse center
     * @param {Vec2D} ellipseRadius - Ellipse radii
     * @returns {Vec2D} Normal vector
     */
    tangentNormalOfEllipse(ellipseOrigin, ellipseRadius) {
        const p = this.sub(ellipseOrigin);
        const xr2 = ellipseRadius.x * ellipseRadius.x;
        const yr2 = ellipseRadius.y * ellipseRadius.y;
        return new Vec2D(p.x / xr2, p.y / yr2).normalize();
    }

    // ============================================
    // Serialization
    // ============================================

    /**
     * Convert to array
     * @returns {number[]} [x, y]
     */
    toArray() {
        return [this.x, this.y];
    }

    /**
     * Convert to JSON
     * @returns {Object} {x, y}
     */
    toJSON() {
        return { x: this.x, y: this.y };
    }

    /**
     * Create from JSON
     * @param {Object} data - {x, y}
     * @returns {Vec2D} New vector
     */
    static fromJSON(data) {
        return new Vec2D(data.x, data.y);
    }

    /**
     * String representation
     * @returns {string} "{x: ..., y: ...}"
     */
    toString() {
        return `{x: ${this.x}, y: ${this.y}}`;
    }
}
